import type { ReactElement } from 'react';

import type { TravelPageDocument } from '@aller/types';

import { BadgeType } from '@/shared/types/travel-search-types';

import { type InterpolateValues, interpolateString } from './formatters';

/**
 * Generated labels object from Contentful Labels
 */
export type Labels = {
    [p: string]: string;
};

export type GetLabelOutput<T extends InterpolateValues> = T extends { [key: string]: string | number } | undefined
    ? string
    : ReactElement;

export const getLabel = (labels?: Labels | null) => {
    return <T extends InterpolateValues = { [key: string]: string }>(key: string, values?: T): GetLabelOutput<T> => {
        const label = labels?.[key];

        if (label) {
            return interpolateString(label, values) as GetLabelOutput<T>;
        }

        return `**${key}**` as GetLabelOutput<T>;
    };
};

export function getTravelBadge(
    travelDoc: TravelPageDocument,
    fewSeatsLeftThreshold: number,
    newTripDaysThreshold: number,
    newDepartureDaysThreshold: number,
): BadgeType | null {
    if (travelDoc.badgeText) {
        return BadgeType.Editorial;
    }

    if (travelDoc.seatsLeft && travelDoc.seatsLeft <= fewSeatsLeftThreshold) {
        return BadgeType.FewSeatsLeft;
    }

    if (travelDoc.hasOffer) {
        return BadgeType.HasOffer;
    }

    const now = new Date();

    if (travelDoc.travelFirstPublished) {
        const tripDaysOld = getDaysDifference(now, travelDoc.travelFirstPublished);
        if (tripDaysOld <= newTripDaysThreshold) {
            return BadgeType.NewTrip;
        }
    }

    if (travelDoc.departureLastCreated) {
        const tripDaysOld = getDaysDifference(now, travelDoc.departureLastCreated);
        if (tripDaysOld <= newDepartureDaysThreshold) {
            return BadgeType.NewDeparture;
        }
    }

    return null;
}

function getDaysDifference(dateA: Date | string, dateB: Date | string): number {
    if (!dateA || !dateB) {
        return 0;
    }

    return (new Date(dateA).getTime() - new Date(dateB).getTime()) / (1000 * 60 * 60 * 24);
}
